import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Term } from './term';
export { Term };
import { termSchema } from '@schemas/business-objects/term';
import { AlternateStudentFormArray } from './alternate-student-form-group';
import { NotAvailableFormArray } from './not-available-form-group';
import { OrganizationFormGroup } from './organization-form-group';
import { SelectedStudentFormArray } from './selected-student-form-group';
import { TermTimeFormArray } from './term-time-form-group';
import { TermTutorFormArray } from './term-tutor-form-group';
import { TestResultFormArray } from './test-result-form-group';
import { UserFormGroup } from './user-form-group';
import type { CalendarDate } from '../base-types';
import type { Attendance } from './attendance';
import type { Curriculum } from './curriculum';
import type { Prescription } from './prescription';
import type { Session } from './session';
import type { Slot } from './slot';
import type { Student } from './student';
import type { StudentAssignment } from './student-assignment';
import type { Tutor } from './tutor';

export class TermFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    startDate: SchemaFormControl;
    endDate: SchemaFormControl;
    timezone: SchemaFormControl;
    organizationId: SchemaFormControl;
    organization: OrganizationFormGroup;
    customerAccountManagerId: SchemaFormControl;
    customerAccountManager: UserFormGroup;
    literacyManagerId: SchemaFormControl;
    literacyManager: UserFormGroup;
    siteLeaderId: SchemaFormControl;
    siteLeader: UserFormGroup;
    selectedStudents: SelectedStudentFormArray;
    alternateStudents: AlternateStudentFormArray;
    termTutors: TermTutorFormArray;
    times: TermTimeFormArray;
    notAvailables: NotAvailableFormArray;
    testResults: TestResultFormArray;
  };

  declare value: Partial<Term>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(termSchema, Term, data, options);
  }

  get object(): Term {
    return new Term(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'organization':
        return new OrganizationFormGroup();
      case 'customerAccountManager':
        return new UserFormGroup();
      case 'literacyManager':
        return new UserFormGroup();
      case 'siteLeader':
        return new UserFormGroup();
      case 'selectedStudents':
        return new SelectedStudentFormArray();
      case 'alternateStudents':
        return new AlternateStudentFormArray();
      case 'termTutors':
        return new TermTutorFormArray();
      case 'times':
        return new TermTimeFormArray();
      case 'notAvailables':
        return new NotAvailableFormArray();
      case 'testResults':
        return new TestResultFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Term[]> {
    return Term.find(filter);
  }

  static async findOne(filter?: any): Promise<Term | null> {
    return Term.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Term> {
    return Term.findById(id, filter);
  }

  static async create(data: Partial<Term>): Promise<Term> {
    return Term.create(data as any);
  }

  static async updateById(id: number, updates: Partial<Term>): Promise<Term> {
    return Term.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Term.deleteById(id);
  }

  async createAttendanceForAllTutors(date: CalendarDate): Promise<any> {
    const instance = new Term(this.getRawValue());
    return instance.createAttendanceForAllTutors(date);
  }
}

export class TermFormArray extends PersistedFormArray {
  readonly entityName = 'Term' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(termSchema, Term, options);
  }

  protected override createItemGroup() {
    return new TermFormGroup();
  }

  get array(): Term[] {
    return this.controls.map((group: TermFormGroup) => group.object);
  }
}

export class TermPersistedArray extends PersistedArray<Term> {
  readonly entityName = 'Term' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Term, options);
  }
}
